import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  HiArrowDown,
  HiArrowLeft,
  HiArrowRight,
  HiArrowUp,
  HiOutlineBan,
  HiOutlineCalendar,
  HiOutlinePencil,
  HiOutlineShare,
  HiRefresh,
} from "react-icons/hi";
import { FaArrowRight } from "react-icons/fa6";
import { CustomButton } from "../../../atoms/CustomButton";
import EmptyState from "../../../atoms/EmptyState";
import Spinner from "../../../atoms/Spinner";
import NoTranslate from "../../../atoms/NoTranslate";
import { StatusBadge } from "../../../atoms/StatusBadge";
import MediaCard from "../../../molecules/MediaCard";
import ConfirmModal from "../../../public/ConfirmModal";
import ShareModal from "../customRegistration/sections/ShareModal";
import useAuth from "../../../../hooks/useAuth";
import { useGetSignupFormsList } from "../../../../hooks/entity";
import { useAgendaAccess } from "../../../../hooks/agenda/useAgendaAccess";
import {
  AgendaType,
  useAgendaPaymentsBlocked,
  useDeactivateAgendaType,
  useGetAgendaTypes,
  useReorderAgendaTypes,
  useUpdateAgendaType,
} from "../../../../hooks/agenda";
import AppointmentTypeDrawer from "./AppointmentTypeDrawer";
import { formatPrice } from "./agendaPayment";
import {
  agendaTypePath,
  bookingPageUrl,
  bookingTunnelUrl,
  descriptionExcerpt,
  useAgendaFailure,
  useFormatDuration,
} from "./agendaTypes";

const NEW_TYPE = "new";

type Share = { link: string; title: string; qrCodeDescription: string };

interface AppointmentTypesPanelProps {
  openTypeId: string | null;
  onOpenType: (typeId: string | null) => void;
}

const AppointmentTypesPanel: React.FC<AppointmentTypesPanelProps> = ({
  openTypeId,
  onOpenType,
}) => {
  const { t, i18n } = useTranslation();
  const formatDuration = useFormatDuration();
  const failure = useAgendaFailure();
  const { user, customer_identification: customerId } = useAuth();
  const entityId = user.entity_identification;
  const { canConfigure: canEdit } = useAgendaAccess(customerId, entityId);
  const paymentsBlocked = useAgendaPaymentsBlocked(customerId, entityId);

  const {
    data: types = [],
    isLoading,
    isError,
    refetch,
  } = useGetAgendaTypes(customerId, entityId);
  const { data: formsData } = useGetSignupFormsList(entityId);
  const deactivate = useDeactivateAgendaType(customerId, entityId);
  const update = useUpdateAgendaType(customerId, entityId);
  const reorder = useReorderAgendaTypes(customerId, entityId);

  const [share, setShare] = useState<Share | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<AgendaType | null>(
    null
  );

  const formNames = useMemo(() => {
    const names = new Map<string, string>();
    (formsData?.forms ?? []).forEach(
      (form: { formId: string; name?: string }) =>
        names.set(
          form.formId,
          form.name || (t("signupForms.unnamedForm") as string)
        )
    );
    return names;
  }, [formsData?.forms, t]);

  const sortedTypes = useMemo(
    () => [...types].sort((a, b) => Number(b.active) - Number(a.active)),
    [types]
  );
  const activeIds = sortedTypes
    .filter((type) => type.active)
    .map((type) => type.id);

  const move = (type: AgendaType, step: -1 | 1) => {
    const order = [...activeIds];
    const from = order.indexOf(type.id);
    const to = from + step;
    if (from < 0 || to < 0 || to >= order.length) return;
    [order[from], order[to]] = [order[to], order[from]];
    reorder.mutate(order, {
      // The card moves in the page, which takes the focus off the button that moved it.
      onSuccess: () => {
        requestAnimationFrame(() => {
          const buttons = Array.from(
            document.querySelectorAll<HTMLButtonElement>(
              `[data-move-type="${type.id}"]`
            )
          );
          (
            buttons.find(
              (button) =>
                !button.disabled && button.dataset.moveStep === String(step)
            ) ?? buttons.find((button) => !button.disabled)
          )?.focus();
        });
      },
      onError: (err) => {
        toast.error(failure(err, "agenda.types.moveError"));
      },
    });
  };

  const shareTypeLink = (type: AgendaType) =>
    setShare({
      link: bookingTunnelUrl(entityId, type.id),
      title: t("agenda.types.shareModal.title") as string,
      qrCodeDescription: t(
        "agenda.types.shareModal.qrCodeDescription"
      ) as string,
    });
  const sharePageLink = () =>
    setShare({
      link: bookingPageUrl(entityId),
      title: t("agenda.types.sharePage") as string,
      qrCodeDescription: t("agenda.types.sharePageQrDescription") as string,
    });

  const drawerType =
    openTypeId && openTypeId !== NEW_TYPE
      ? types.find((type) => type.id === openTypeId) ?? null
      : null;
  const drawerOpen = openTypeId === NEW_TYPE ? canEdit : Boolean(drawerType);

  const onDeactivate = () => {
    if (!pendingDeactivate) return;
    deactivate.mutate(pendingDeactivate.id, {
      onSuccess: () => {
        toast.success(t("agenda.types.deactivated") as string);
        setPendingDeactivate(null);
      },
      onError: (err) => {
        toast.error(failure(err, "agenda.types.deactivateError"));
      },
    });
  };

  const onReactivate = (type: AgendaType) =>
    update.mutate(
      { id: type.id, patch: { active: true } },
      {
        onSuccess: () => {
          toast.success(t("agenda.types.reactivated") as string);
        },
        onError: (err) => {
          toast.error(failure(err, "agenda.types.reactivateError"));
        },
      }
    );

  const typeActions = (type: AgendaType) => (
    <>
      {type.active && (
        <CustomButton
          onClick={() => shareTypeLink(type)}
          variant="secondary"
          size="sm"
          aria-label={`${t("agenda.types.share")} ${type.title}`}
          comp={
            <>
              <HiOutlineShare size={14} aria-hidden="true" />
              <span className="hidden sm:inline">
                {t("agenda.types.shareAction")}
              </span>
            </>
          }
        />
      )}
      {canEdit && (
        <CustomButton
          onClick={() => onOpenType(type.id)}
          variant="secondary"
          size="sm"
          aria-label={`${t("common.edit")} ${type.title}`}
          comp={
            <>
              <HiOutlinePencil size={14} aria-hidden="true" />
              <span className="hidden sm:inline">{t("common.edit")}</span>
            </>
          }
        />
      )}
      {canEdit &&
        (type.active ? (
          <CustomButton
            onClick={() => setPendingDeactivate(type)}
            variant="secondary"
            size="sm"
            className="text-red-600"
            aria-label={`${t("agenda.types.deactivate")} ${type.title}`}
            comp={
              <>
                <HiOutlineBan size={14} aria-hidden="true" />
                <span className="hidden sm:inline">
                  {t("agenda.types.deactivate")}
                </span>
              </>
            }
          />
        ) : (
          <CustomButton
            onClick={() => onReactivate(type)}
            disabled={update.isLoading}
            variant="secondary"
            size="sm"
            aria-label={`${t("agenda.types.reactivate")} ${type.title}`}
            comp={
              <>
                <HiRefresh size={14} aria-hidden="true" />
                <span className="hidden sm:inline">
                  {t("agenda.types.reactivate")}
                </span>
              </>
            }
          />
        ))}
      {canEdit && type.active && activeIds.length > 1 && (
        <>
          <CustomButton
            onClick={() => move(type, -1)}
            disabled={reorder.isLoading || activeIds[0] === type.id}
            variant="secondary"
            size="sm"
            tooltip={t("agenda.types.moveBefore") as string}
            aria-label={
              t("agenda.types.moveBeforeOf", { title: type.title }) as string
            }
            data-move-type={type.id}
            data-move-step="-1"
            comp={
              <>
                <HiArrowUp size={14} aria-hidden="true" className="md:hidden" />
                <HiArrowLeft
                  size={14}
                  aria-hidden="true"
                  className="hidden md:block"
                />
              </>
            }
          />
          <CustomButton
            onClick={() => move(type, 1)}
            disabled={
              reorder.isLoading || activeIds[activeIds.length - 1] === type.id
            }
            variant="secondary"
            size="sm"
            tooltip={t("agenda.types.moveAfter") as string}
            aria-label={
              t("agenda.types.moveAfterOf", { title: type.title }) as string
            }
            data-move-type={type.id}
            data-move-step="1"
            comp={
              <>
                <HiArrowDown
                  size={14}
                  aria-hidden="true"
                  className="md:hidden"
                />
                <HiArrowRight
                  size={14}
                  aria-hidden="true"
                  className="hidden md:block"
                />
              </>
            }
          />
        </>
      )}
    </>
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-10">
        <Spinner size="md" />
      </div>
    );
  } else if (isError) {
    body = (
      <EmptyState
        bordered
        title={t("agenda.types.loadError")}
        action={
          <CustomButton
            variant="secondary"
            title={t("agenda.retry") as string}
            onClick={() => refetch()}
          />
        }
      />
    );
  } else if (sortedTypes.length === 0) {
    body = (
      <EmptyState
        bordered
        icon={<HiOutlineCalendar size={24} />}
        title={t("agenda.types.emptyTitle")}
        description={t("agenda.types.emptyDescription")}
      />
    );
  } else {
    body = (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {sortedTypes.map((type) => {
          const excerpt = descriptionExcerpt(type.description);
          return (
            <MediaCard
              key={type.id}
              imageUrl={type.image_url}
              imageAlt={type.title}
              placeholderIcon={
                <HiOutlineCalendar
                  className="text-gray-300"
                  size={48}
                  aria-hidden="true"
                />
              }
              badge={
                !type.active ? (
                  <StatusBadge color="gray" dot>
                    {t("agenda.types.inactive")}
                  </StatusBadge>
                ) : (
                  (!type.listed || (type.payment && paymentsBlocked)) && (
                    <div className="flex flex-col items-end gap-1">
                      {type.payment && paymentsBlocked && (
                        <StatusBadge color="warning" dot>
                          {t("agenda.types.notBookable")}
                        </StatusBadge>
                      )}
                      {!type.listed && (
                        <StatusBadge color="gray" dot>
                          {t("agenda.types.unlisted")}
                        </StatusBadge>
                      )}
                    </div>
                  )
                )
              }
              actions={type.active || canEdit ? typeActions(type) : undefined}
            >
              <div className="min-w-0">
                <Link
                  to={agendaTypePath(type.id)}
                  className="group flex min-w-0 items-center gap-1 text-sm font-semibold text-gray-900 hover:text-blue-700"
                >
                  <span className="truncate">
                    <NoTranslate>{type.title}</NoTranslate>
                  </span>
                  <FaArrowRight className="h-2.5 w-2.5 shrink-0 text-gray-300 group-hover:text-blue-700" />
                </Link>
                {excerpt && (
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                    <NoTranslate>{excerpt}</NoTranslate>
                  </p>
                )}
              </div>
              <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-gray-500">
                  {t("agenda.types.durationLabel")}
                </dt>
                <dd className="text-gray-700">
                  {formatDuration(type.duration_min)}
                </dd>
                <dt className="text-gray-500">
                  {t("agenda.types.priceLabel")}
                </dt>
                <dd className="tabular-nums text-gray-700">
                  {type.payment
                    ? formatPrice(
                        type.payment.amount,
                        type.payment.currency,
                        i18n.language
                      )
                    : t("agenda.types.free")}
                </dd>
                <dt className="text-gray-500">
                  {t("agenda.types.membersLabel")}
                </dt>
                <dd className="min-w-0 truncate text-gray-700">
                  {type.members.length > 0 ? (
                    <NoTranslate>
                      {type.members.map((m) => m.name).join(", ")}
                    </NoTranslate>
                  ) : (
                    t("agenda.types.noMember")
                  )}
                </dd>
                <dt className="text-gray-500">
                  {t("agenda.types.signupFormLabel")}
                </dt>
                <dd className="min-w-0 truncate text-gray-700">
                  {type.signup_form_id ? (
                    <NoTranslate>
                      {formNames.get(type.signup_form_id) ??
                        t("signupForms.unnamedForm")}
                    </NoTranslate>
                  ) : (
                    t("agenda.types.registeredOnly")
                  )}
                </dd>
              </dl>
            </MediaCard>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="min-w-0 flex-1 sm:basis-64">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("agenda.types.title")}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {t("agenda.types.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CustomButton
            variant="secondary"
            onClick={sharePageLink}
            title={t("agenda.types.sharePage") as string}
            icon={<HiOutlineShare size={16} aria-hidden="true" />}
          />
          {canEdit && (
            <CustomButton
              variant="primary"
              icon="plus"
              title={t("agenda.types.new") as string}
              onClick={() => onOpenType(NEW_TYPE)}
            />
          )}
        </div>
      </div>

      {body}

      <AppointmentTypeDrawer
        open={drawerOpen}
        onClose={() => onOpenType(null)}
        type={drawerType}
      />

      <ShareModal
        showShareModal={share !== null}
        setShowShareModal={(value) => {
          if (!value) setShare(null);
        }}
        generatedLink={share?.link ?? ""}
        title={share?.title}
        qrCodeTitle={t("agenda.types.shareModal.qrCodeTitle") as string}
        qrCodeDescription={share?.qrCodeDescription}
      />

      <ConfirmModal
        show={pendingDeactivate !== null}
        onClose={() => setPendingDeactivate(null)}
        title={t("agenda.types.deactivateConfirm.title")}
        description={t("agenda.types.deactivateConfirm.description", {
          title: pendingDeactivate?.title ?? "",
        })}
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={() => setPendingDeactivate(null)}
        confirmBtnName={t("agenda.types.deactivate")}
        confirmBtnFn={onDeactivate}
        ConfirmBtnColor="red"
        isLoading={deactivate.isLoading}
      />
    </div>
  );
};

export default AppointmentTypesPanel;
